import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { translate, type Dictionary, type StringKey } from './translator';
import { DEFAULT_LOCALE, type Locale } from './locales';

export interface I18nContextValue {
  locale: Locale;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  dictionary: Dictionary;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<I18nContextValue>(() => {
    const dict = (locale === DEFAULT_LOCALE
      ? // eslint-disable-next-line @typescript-eslint/no-var-requires
        null
      : null) as unknown as Dictionary;
    return {
      locale,
      dictionary: dict as unknown as Dictionary,
      t: (key, vars) => translate(locale, key, vars),
    };
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return {
      locale: DEFAULT_LOCALE,
      dictionary: {} as Dictionary,
      t: (key, vars) => translate(DEFAULT_LOCALE, key, vars),
    };
  }
  return ctx;
}

export function useT(): (key: StringKey, vars?: Record<string, string | number>) => string {
  return useI18n().t;
}
